import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from 'hooks'
import { DataTable, DataTablePagination } from 'components/common'
import { SetSelectedColumns } from 'types/dataTable'
import { RowClickedEvent } from '@ag-grid-community/core'
import {
  ORDER_TABLE_FIELDS,
  PAGE_POS_OPEN_ORDERS_DETAILS,
} from 'utils/constants'
import { fetchPosOpenOrders } from 'utils/api/openOrderApi'
import { openOrderColumns } from './utils'

type Props = {
  setSelectedColumns: SetSelectedColumns
  loading: boolean
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
}

const POLL_INTERVAL = 10000 // 10 seconds

const OpenOrdersTable = ({
  setSelectedColumns,
  loading,
  setLoading,
}: Props) => {
  const [ordersData, setOrdersData] = useState<any>({})

  const { search } = useLocation()
  const { getAllQueries } = useQuery()
  const navigate = useNavigate()

  const getOpenOrders = async () => {
    setLoading(true)

    const {
      limit,
      search: searchTerm,
      page,
      openOrderFilters,
    } = getAllQueries()

    try {
      const { data } = await fetchPosOpenOrders({
        limit: limit || 10,
        page: page || 1,
        search: searchTerm,
        filters: openOrderFilters,
      })
      setSelectedColumns(data?.tableConfig?.openOrder || [])
      setOrdersData({ ...data })
    } catch (error) {
      setOrdersData({ docs: [] })
    } finally {
      setLoading(false)
    }
  }

  const onRowClicked = (event: RowClickedEvent) => {
    navigate(
      `${PAGE_POS_OPEN_ORDERS_DETAILS.path}/${
        event.data[ORDER_TABLE_FIELDS.ORDER_NO.value]
      }`,
    )
  }

  useEffect(() => {
    getOpenOrders()

    const intervalId = setInterval(() => {
      getOpenOrders()
    }, POLL_INTERVAL)

    return () => clearInterval(intervalId)
  }, [search])

  return (
    <>
      <DataTable
        columns={loading ? [] : openOrderColumns}
        rows={loading ? null : ordersData?.docs}
        identifier={ORDER_TABLE_FIELDS.ORDER_NO.value}
        onRowClicked={onRowClicked}
        activeColumnsKey="openOrder"
        activeColumns={ordersData?.tableConfig?.openOrder || []}
      />

      <DataTablePagination
        disableGoToLastPage
        shouldDisplayTotalPages={false}
        currentPage={ordersData?.page}
        totalPages={ordersData?.totalPages}
        hasNextPage={ordersData?.hasNextPage}
        hasPrevPage={ordersData?.hasPrevPage}
        limit={ordersData?.limit}
        nextPage={ordersData?.nextPage}
        prevPage={ordersData?.prevPage}
      />
    </>
  )
}

export default OpenOrdersTable
